import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useCanvas } from '../../hooks/useCanvas';
import { useExportNotifications } from '../../hooks/useExportNotifications';
import CustomInput from '../UI/CustomInput';
import ExportProgressModal from '../Canvas/CanvasToolbar/ExportProgressModal';
import { Play, Pause, MoreHorizontal, Download, Check, X, Loader2 } from 'lucide-react';
import {
  downloadVideo,
  exportCanvasVideoWithPuppeteer as exportPuppeteer,
  getExportFilename,
  getExportResolution,
} from '../../utils/puppeteerExportUtils';
import { projectsAPI } from '../../services/api';

const LiteTimeline = ({ onPinControls }) => {
  const {
    currentTime,
    duration,
    isPlaying,
    togglePlayback,
    setCurrentTime,
    stopPlaybackLoop,
    projectName,
    setProjectName,
    canvasSize,
    canvasElements,
    canvasBackgroundColor,
  } = useCanvas();

  const { projectId } = useParams();
  const { notifications, startPolling, stopPolling, removeNotification } = useExportNotifications();

  const containerRef = useRef(null);
  const progressBarRef = useRef(null);
  const menuRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hoverTime, setHoverTime] = useState(null);
  const [hoverX, setHoverX] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [isCompact, setIsCompact] = useState(false);

  // Detect narrow container (portrait stage) via ResizeObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setIsCompact(entry.contentRect.width < 400);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Export state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportStage, setExportStage] = useState('idle');
  const [exportProgress, setExportProgress] = useState(0);
  const [exportMessage, setExportMessage] = useState('');
  const [exportError, setExportError] = useState(null);
  const [exportDownloadUrl, setExportDownloadUrl] = useState(null);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [pollingHandedOff, setPollingHandedOff] = useState(false);

  // Calculate content duration (without padding)
  const durationPadding = 3;
  const contentDuration = Math.max(0, duration - durationPadding);

  // Progress percentage
  const progress = contentDuration > 0 ? (currentTime / contentDuration) * 100 : 0;
  const clampedProgress = Math.min(100, Math.max(0, progress));

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimeAgo = (timestamp) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  // Determine if an export is actively in progress
  const isExportActive = isExportModalOpen && ['preparing', 'downloading', 'rendering', 'encoding'].includes(exportStage);

  // Pin controls when menu is open, export is actively in progress, or name is being edited
  useEffect(() => {
    const shouldPin = menuOpen || isExportActive || isNameEditing;
    if (onPinControls) onPinControls(shouldPin);
  }, [menuOpen, isExportActive, isNameEditing, onPinControls]);

  // --- Close menu on outside click ---
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // --- Progress bar interactions ---
  const updateTimeFromEvent = useCallback((e) => {
    if (!progressBarRef.current) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const relativeX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percentage = relativeX / rect.width;
    const newTime = percentage * contentDuration;
    stopPlaybackLoop();
    setCurrentTime(Math.max(0, Math.min(contentDuration, newTime)));
  }, [contentDuration, setCurrentTime, stopPlaybackLoop]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    updateTimeFromEvent(e);
  }, [updateTimeFromEvent]);

  const handleMouseMove = useCallback((e) => {
    if (!progressBarRef.current) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const relativeX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percentage = relativeX / rect.width;
    setHoverTime(percentage * contentDuration);
    setHoverX(relativeX);
    if (isDragging) updateTimeFromEvent(e);
  }, [isDragging, updateTimeFromEvent, contentDuration]);

  const handleMouseLeave = useCallback(() => {
    if (!isDragging) setHoverTime(null);
  }, [isDragging]);

  useEffect(() => {
    if (isDragging) {
      const onMouseMove = (e) => {
        if (!progressBarRef.current) return;
        const rect = progressBarRef.current.getBoundingClientRect();
        const relativeX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
        const percentage = relativeX / rect.width;
        const newTime = percentage * contentDuration;
        setCurrentTime(Math.max(0, Math.min(contentDuration, newTime)));
      };
      const onMouseUp = () => setIsDragging(false);
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      return () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
    }
  }, [isDragging, contentDuration, setCurrentTime]);

  // --- Project name ---
  const handleChangeProjectName = useCallback(async (value) => {
    setProjectName(value);
    try {
      await projectsAPI.changeProjectName(projectId, value);
    } catch (err) {
      console.error('Failed to update project name:', err);
    }
  }, [projectId, setProjectName]);

  // --- Export ---
  const handleExportVideo = useCallback(async (format = 'mp4') => {
    setMenuOpen(false);
    setIsExportModalOpen(true);
    setExportStage('preparing');
    setExportProgress(0);
    setExportError(null);
    setExportDownloadUrl(null);
    setCurrentJobId(null);
    setPollingHandedOff(false);

    try {
      const { width: exportWidth, height: exportHeight } = getExportResolution(canvasSize);

      const url = await exportPuppeteer(
        {
          canvasSize,
          canvasElements,
          backgroundColor: canvasBackgroundColor || '#000000',
          duration: contentDuration,
          projectId,
          projectName: projectName || 'Untitled',
          fps: format === 'gif' ? 12 : 25,
          exportWidth,
          exportHeight,
          format,
        },
        (stage, _current, _total, percentage, message, jobId) => {
          setExportStage(stage);
          setExportProgress(percentage);
          setExportMessage(message);
          if (jobId && !currentJobId) {
            setCurrentJobId(jobId);
            startPolling(jobId, projectName || 'Untitled', format);
            setPollingHandedOff(true);
          }
        }
      );

      setExportDownloadUrl(url);
      setExportStage('complete');
    } catch (error) {
      console.error('[Export] Failed:', error);
      setExportStage('error');
      setExportError('Export failed');
    }
  }, [canvasSize, canvasElements, canvasBackgroundColor, contentDuration, projectId, projectName, currentJobId, startPolling]);

  const handleDownloadNotification = useCallback((downloadUrl, jobId) => {
    const notification = notifications.find(n => n.jobId === jobId);
    const format = notification?.format || 'mp4';
    const name = notification?.projectName || projectName || 'Untitled';
    const filename = getExportFilename(name, format);
    downloadVideo(downloadUrl, filename);
  }, [notifications, projectName]);

  const handleDownloadExportedVideo = useCallback((downloadUrl, format = 'mp4') => {
    if (downloadUrl) {
      const filename = getExportFilename(projectName || 'Untitled', format);
      downloadVideo(downloadUrl, filename);
    }
  }, [projectName]);

  const handleCancelExport = useCallback(async () => {
    if (currentJobId) {
      try {
        await projectsAPI.cancelPuppeteerExport({ jobId: currentJobId });
        stopPolling(currentJobId);
        removeNotification(currentJobId);
      } catch (error) {
        console.error('[Export] Failed to cancel:', error);
      }
    }
    setIsExportModalOpen(false);
    setTimeout(() => {
      setExportStage('idle');
      setExportProgress(0);
      setExportError(null);
      setExportDownloadUrl(null);
      setCurrentJobId(null);
    }, 300);
  }, [currentJobId, stopPolling, removeNotification]);

  const handleCloseExportModal = useCallback(() => {
    if (!['downloading', 'rendering', 'encoding'].includes(exportStage)) {
      setIsExportModalOpen(false);
      setTimeout(() => {
        setExportStage('idle');
        setExportProgress(0);
        setExportError(null);
        setExportDownloadUrl(null);
        setCurrentJobId(null);
        setPollingHandedOff(false);
      }, 300);
    }
  }, [exportStage]);

  // Sync modal with notification polling
  useEffect(() => {
    if (isExportModalOpen && currentJobId && pollingHandedOff) {
      const notification = notifications.find(n => n.jobId === currentJobId);
      if (notification) {
        const isTerminal = notification.status === 'complete' || notification.status === 'error';
        const stageMapping = {
          'downloading_media': 'downloading',
          'rendering': 'rendering',
          'encoding': 'encoding',
          'complete': 'complete',
          'error': 'error',
        };
        const newProgress = notification.progress || 0;
        if (newProgress >= exportProgress || isTerminal) {
          const frontendStage = stageMapping[notification.stage] || notification.stage;
          if (frontendStage && frontendStage !== 'processing') {
            setExportStage(frontendStage);
          }
          setExportProgress(newProgress);
          if (notification.stage) setExportMessage(`${notification.stage}... ${Math.round(newProgress)}%`);
          if (notification.status === 'complete' && notification.downloadUrl) setExportDownloadUrl(notification.downloadUrl);
          if (notification.status === 'error' && notification.error) setExportError(notification.error);
        }
      }
    }
  }, [notifications, isExportModalOpen, currentJobId, pollingHandedOff, exportProgress]);

  // Count active notifications for badge
  const activeNotificationCount = notifications.length;

  return (
    <>
      <div ref={containerRef} className={`w-full flex flex-col select-none pb-2 pt-1 ${isCompact ? 'px-1.5' : 'px-2 sm:px-3'}`}>
        {/* Row 1: Progress bar */}
        <div
          ref={progressBarRef}
          className="w-full relative h-4 flex items-center cursor-pointer group"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <div className="w-full h-[3px] group-hover:h-1 transition-all rounded-full bg-white/20 relative overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full bg-ag-green rounded-full"
              style={{ width: `${clampedProgress}%` }}
            />
            {hoverTime !== null && !isDragging && (
              <div
                className="absolute top-0 left-0 h-full bg-white/15 rounded-full"
                style={{ width: `${(hoverTime / contentDuration) * 100}%` }}
              />
            )}
          </div>
          <div
            className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-ag-green shadow-md transition-opacity ${isDragging ? 'opacity-100 scale-125' : 'opacity-0 group-hover:opacity-100'}`}
            style={{ left: `calc(${clampedProgress}% - 6px)` }}
          />
          {hoverTime !== null && (
            <div
              className="absolute -top-6 bg-black/80 text-white text-[10px] px-1.5 py-0.5 rounded pointer-events-none font-mono"
              style={{ left: `${hoverX}px`, transform: 'translateX(-50%)' }}
            >
              {formatTime(hoverTime)}
            </div>
          )}
        </div>

        {/* Row 2: Time display */}
        <div className="flex items-center mt-0.5">
          <span className={`text-gray-400 font-mono ${isCompact ? 'text-[10px]' : 'text-[11px] sm:text-[13px]'}`}>
            {formatTime(currentTime)} / {formatTime(contentDuration)}
          </span>
        </div>

        {/* Row 3: Play button, project name, 3-dot menu */}
        <div className="flex items-center mt-1 min-w-0">
          {/* Play/Pause */}
          <button
            onClick={(e) => { e.stopPropagation(); togglePlayback(); }}
            className={`flex-shrink-0 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors ${isCompact ? 'w-6 h-6' : 'w-7 h-7 sm:w-8 sm:h-8'}`}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <Pause size={isCompact ? 14 : 16} className="text-gray-400 sm:hidden" fill="currentColor" />
            ) : (
              <Play size={isCompact ? 14 : 16} className="text-gray-400 ml-0.5 sm:hidden" fill="currentColor" />
            )}
            {isPlaying ? (
              <Pause size={18} className="text-gray-400 hidden sm:block" fill="currentColor" />
            ) : (
              <Play size={18} className="text-gray-400 ml-0.5 hidden sm:block" fill="currentColor" />
            )}
          </button>

          {/* Editable project name — pointer-events disabled while scrubbing to prevent accidental selection */}
          <CustomInput
            defaultValue={projectName || 'Untitled Project'}
            placeholder="Project Name"
            className={`bg-transparent text-gray-400 font-medium
                       placeholder-gray-500 focus:outline-none focus:bg-white/10
                       rounded py-1 min-w-0 flex-1 truncate border border-transparent
                       hover:border-white/20 focus:border-ag-green transition-colors
                       ${isCompact ? 'ml-0.5 px-1 text-[11px]' : 'ml-1 sm:ml-2 px-1 sm:px-2 text-xs sm:text-lg'}`}
            style={isDragging ? { pointerEvents: 'none' } : undefined}
            onFocus={() => setIsNameEditing(true)}
            onUnfocus={(value) => { setIsNameEditing(false); handleChangeProjectName(value); }}
            onBlur={() => setIsNameEditing(false)}
            onClick={(e) => e.stopPropagation()}
          />

          {/* 3-dot menu with export + notifications merged */}
          <div ref={menuRef} className={`relative flex-shrink-0 ${isCompact ? 'mr-0' : 'mr-1 sm:mr-4'}`}>
            <button
              className={`flex items-center justify-center rounded-full hover:bg-white/10 transition-colors relative ${isCompact ? 'w-6 h-6' : 'w-8 h-8'}`}
              title="More options"
              onClick={(e) => { e.stopPropagation(); setMenuOpen(prev => !prev); }}
            >
              <MoreHorizontal size={isCompact ? 18 : 50} className="text-gray-400" />
              {/* Badge for active notifications */}
              {activeNotificationCount > 0 && (
                <span className="absolute -top-1 -right-2 w-4 h-4 bg-ag-green rounded-full flex items-center justify-center text-black text-[9px] font-bold">
                  {activeNotificationCount}
                </span>
              )}
            </button>

            {menuOpen && (
              <div className={`absolute bottom-full right-0 mb-2 bg-ag-black-800 border border-ag-black-400 rounded-lg shadow-xl z-50 overflow-hidden ${isCompact ? 'w-36' : 'w-44 sm:w-72'}`}>
                {/* Export actions */}
                <div className="py-1">
                  <button
                    className={`w-full text-left text-white hover:bg-ag-black-600 transition-colors flex items-center ${isCompact ? 'px-2.5 py-1.5 text-[11px] gap-1.5' : 'px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm gap-2 sm:gap-2.5'}`}
                    onClick={() => handleExportVideo('mp4')}
                  >
                    <Download size={isCompact ? 12 : 14} className="text-gray-400" />
                    <span>Export as MP4</span>
                  </button>
                  <button
                    className={`w-full text-left text-white hover:bg-ag-black-600 transition-colors flex items-center ${isCompact ? 'px-2.5 py-1.5 text-[11px] gap-1.5' : 'px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm gap-2 sm:gap-2.5'}`}
                    onClick={() => handleExportVideo('gif')}
                  >
                    <Download size={isCompact ? 12 : 14} className="text-gray-400" />
                    <span>Export as GIF</span>
                  </button>
                </div>

                {/* Divider + Notifications section (only if there are any) */}
                {notifications.length > 0 && (
                  <>
                    <div className="border-t border-ag-black-400" />
                    <div className={`flex items-center justify-between ${isCompact ? 'px-2.5 py-1' : 'px-3 sm:px-4 py-1.5 sm:py-2'}`}>
                      <span className={`text-gray-500 uppercase tracking-wider font-semibold ${isCompact ? 'text-[9px]' : 'text-[10px] sm:text-[11px]'}`}>Exports</span>
                    </div>
                    <div className={`overflow-y-auto ${isCompact ? 'max-h-32' : 'max-h-40 sm:max-h-52'}`}>
                      {notifications.map((n) => (
                        <div
                          key={n.jobId}
                          className={`hover:bg-ag-black-700 transition-colors flex items-center ${isCompact ? 'px-2.5 py-1.5 gap-1.5' : 'px-3 sm:px-4 py-2 sm:py-2.5 gap-2 sm:gap-3'}`}
                        >
                          {/* Status icon */}
                          <div className="flex-shrink-0">
                            {n.status === 'complete' && <Check size={16} className="text-ag-green" />}
                            {n.status === 'error' && <X size={16} className="text-red-400" />}
                            {n.status === 'processing' && <Loader2 size={16} className="text-ag-green animate-spin" />}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-white truncate">{n.projectName || 'Export'}</p>
                            {n.status === 'processing' && (
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex-1 bg-ag-black-400 rounded-full h-1">
                                  <div
                                    className="bg-ag-green h-full rounded-full transition-all duration-300"
                                    style={{ width: `${n.progress || 0}%` }}
                                  />
                                </div>
                                <span className="text-[10px] text-gray-500 flex-shrink-0">{Math.round(n.progress || 0)}%</span>
                              </div>
                            )}
                            {n.status === 'complete' && (
                              <p className="text-[10px] text-gray-500 mt-0.5">Ready · {formatTimeAgo(n.timestamp)}</p>
                            )}
                            {n.status === 'error' && (
                              <p className="text-[10px] text-red-400 mt-0.5 truncate">{n.error || 'Failed'}</p>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex-shrink-0 flex items-center gap-1">
                            {n.status === 'complete' && n.downloadUrl && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDownloadNotification(n.downloadUrl, n.jobId); }}
                                className="p-1.5 bg-ag-green hover:bg-green-500 rounded transition-colors"
                                title="Download"
                              >
                                <Download size={12} className="text-black" />
                              </button>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); removeNotification(n.jobId); }}
                              className="p-1.5 hover:bg-ag-black-600 rounded transition-colors"
                              title="Dismiss"
                            >
                              <X size={12} className="text-gray-500" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Export Progress Modal */}
      <ExportProgressModal
        isOpen={isExportModalOpen}
        stage={exportStage}
        progress={exportProgress}
        message={exportMessage}
        error={exportError}
        downloadUrl={exportDownloadUrl}
        onClose={handleCloseExportModal}
        onDownload={handleDownloadExportedVideo}
        onCancel={handleCancelExport}
      />
    </>
  );
};

export default LiteTimeline;
